import { useEffect, useState } from 'react';
import { Box, Button, Heading, Modal, TextButton } from '@zillow/constellation-10';

import DeactivationReasonStep from './components/DeactivationReasonStep';

import { type DeactivationReason, STEPS } from './constants';

type DeactivationAnswer = DeactivationReason | null;

type DeactivateListingModalProps = {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
};

const DeactivateListingModal = ({ open = false, onOpenChange }: DeactivateListingModalProps) => {
  const [step, setStep] = useState(STEPS.DEACTIVATION_REASON);
  const [selectedAnswer, setSelectedAnswer] = useState<DeactivationAnswer>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal is closed
  useEffect(() => {
    if (!open) {
      setStep(STEPS.DEACTIVATION_REASON);
      setSelectedAnswer(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleAnswerCardClick = (answer: DeactivationAnswer) => {
    // TODO: Implement answer selection logic
    setSelectedAnswer(answer);
    // Answer card clicked - tracking: FOUND_TENANT, TENANT_RENEWED, or STILL_LOOKING_PAUSING

    // Pseudo code for future implementation:
    // - Track which card was clicked (FOUND_TENANT, TENANT_RENEWED, or STILL_LOOKING_PAUSING)
    // - Progress to next step
  };

  const handleCancel = () => {
    setSelectedAnswer(null);
    onOpenChange(false);
  };

  const handleDeactivate = async () => {
    if (!selectedAnswer) return;

    setIsSubmitting(true);
    try {
      // TODO: Implement deactivation API call
      // Deactivating listing with reason: selectedAnswer

      // Pseudo code for future implementation:
      // - Call deactivation API with selected reason
      // - Handle success/error states
      // - Navigate to next step or close modal
      // - Show confirmation message

      // For now, just close the modal
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDeactivateDisabled = selectedAnswer === null || isSubmitting;

  return (
    <Modal
      data-testid="DeactivateListingModal"
      open={open}
      onOpenChange={onOpenChange}
      header={
        <Heading level={5} css={{ textAlign: 'left' }}>
          Why are you deactivating this listing?
        </Heading>
      }
      body={
        <>
          {step === STEPS.DEACTIVATION_REASON && (
            <DeactivationReasonStep selectedAnswer={selectedAnswer} onAnswerSelect={handleAnswerCardClick} />
          )}
        </>
      }
      footer={
        <Box
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '200',
            width: '100%',
          }}
        >
          <TextButton
            data-testid="CancelButton"
            css={{ paddingLeft: '400', paddingRight: '400' }}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </TextButton>

          <Button
            data-testid="DeactivateButton"
            emphasis="filled"
            tone="brand"
            onClick={handleDeactivate}
            disabled={isDeactivateDisabled}
            loading={isSubmitting}
          >
            Deactivate listing
          </Button>
        </Box>
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default DeactivateListingModal;
